import {Box, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {ChangeEvent, useId} from 'react';

import {suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {getDataAttribute} from '../../utils/getDataAttribute';
import {HelperText} from '../HelperText/HelperText';

export interface SwitchProps extends FormControlProps<boolean>, TestIdProps {
  isLoading?: boolean;
  // A unique ID for the input element, allowing it to be associated with a <label> element in another component.
  externalLabelId?: string;
}

export function Switch(props: SwitchProps) {
  const id = useId();
  const [isFocused, handleFocus, handleBlur] = useBoolean(false);

  const isInvalid = props.isInvalid || !!props.errorMessage;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (props.isDisabled || props.isLoading) {
      event.preventDefault();
      return;
    }

    props.onChange?.(!props.value);
  };

  const handleFocusEvent = (event: React.FocusEvent<HTMLInputElement>) => {
    handleFocus();
    props.onFocus?.(event);
  };

  const handleBlurEvent = (event: React.FocusEvent<HTMLInputElement>) => {
    handleBlur();
    props.onBlur?.(event);
  };

  const rootProps = {
    'data-disabled': getDataAttribute(props.isDisabled),
    'data-checked': getDataAttribute(props.value),
    'data-invalid': getDataAttribute(isInvalid),
  };

  const checkboxProps = {
    'data-checked': getDataAttribute(props.value),
    'data-disabled': getDataAttribute(props.isDisabled),
    'data-invalid': getDataAttribute(isInvalid),
    'data-focus': getDataAttribute(isFocused),
  };

  return (
    <VStack>
      <SwitchWrapper
        htmlFor={props.externalLabelId ?? id}
        {...rootProps}
        data-testid={suffixTestId('switchWrapper', props)}
      >
        <SwitchInput
          id={props.externalLabelId ?? id}
          type="checkbox"
          role="switch"
          checked={props.value}
          onChange={handleChange}
          onFocus={handleFocusEvent}
          onBlur={handleBlurEvent}
          disabled={props.isDisabled || props.isLoading}
          required={props.isRequired}
          aria-checked={props.value}
          aria-busy={props.isLoading}
          aria-disabled={props.isDisabled || props.isLoading}
          data-testid={suffixTestId('switchInput', props)}
        />
        <SwitchTrack {...checkboxProps} data-testid={suffixTestId('switchTrack', props)}>
          <SwitchThumb
            data-checked={getDataAttribute(props.value)}
            data-testid={suffixTestId('switchThumb', props)}
          />
        </SwitchTrack>
        <Show when={props.label}>
          <Box paddingLeft={3}>
            <Show when={props.label}>
              <Text size="small" data-testid={suffixTestId('label', props)}>
                {props.label}
              </Text>
            </Show>
            <Show when={props.helperText}>
              <HelperText
                helperText={props.helperText}
                data-testid={suffixTestId('helper', props)}
              />
            </Show>
            <Show when={props.errorMessage}>
              <HelperText
                errorMessage={props.errorMessage}
                data-testid={suffixTestId('errorMessage', props)}
              />
            </Show>
          </Box>
        </Show>
      </SwitchWrapper>
    </VStack>
  );
}

const SwitchWrapper = styled.label`
  position: relative;
  display: flex;
  cursor: pointer;

  &[data-disabled='true'] {
    cursor: not-allowed;
  }
`;

const SwitchInput = styled.input`
  border: 0px;
  clip: rect(0px, 0px, 0px, 0px);
  height: 0px;
  width: 0px;
  margin: 0px;
  padding: 0px;
  overflow: hidden;
  white-space: nowrap;
  position: absolute;
`;

// Disabled warning for px units because we can't get 2px from theme.getSize.
// The minimum padding we can get is 4px.
// eslint-disable-next-line eag/no-css-property
const SwitchTrack = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  justify-content: flex-start;
  box-sizing: content-box;
  cursor: pointer;
  border-radius: ${({theme}) => theme.radii.large};
  padding: 2px;
  width: ${({theme}) => theme.getSize(8)};
  height: ${({theme}) => theme.getSize(4)};
  transition: 150ms;
  background-color: ${({theme}) => theme.components.Switch.trackStyle.backgroundColor};

  &:hover {
    box-shadow: 0 0 0 4px ${({theme}) => theme.colors.palettes.blue[20][100]};
  }

  &:active {
    box-shadow: 0 0 0 4px ${({theme}) => theme.colors.palettes.blue[30][100]};
  }

  &[data-focus='true'] {
    outline: none;
    box-shadow: 0 0 0 4px ${({theme}) => theme.colors.palettes.blue[40][100]};
  }

  &[data-checked='true'] {
    background-color: ${({theme}) => theme.colors.general.accent};
  }

  &[data-disabled='true'] {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SwitchThumb = styled.span`
  background: ${({theme}) => theme.colors.general.white};
  transition: transform 200ms;
  width: ${({theme}) => theme.getSize(4)};
  height: ${({theme}) => theme.getSize(4)};
  border-radius: ${({theme}) => theme.radii.large};
  box-shadow: ${({theme}) => theme.shadows.elevation_1};

  &[data-checked='true'] {
    transform: translateX(${({theme}) => theme.getSize(4)});
  }
`;
